import {
  Button,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { useOutsourceCategoriesQuery, useStaffQuery } from "../api/queries";
import {
  OutsourceBankFields,
  OutsourceCategoryTypeSection,
  OutsourceContactFields,
  OutsourceFormPreview,
  OutsourceIdentityFields,
  OutsourceNotesField,
  OutsourceStaffPickField,
  applyStaffToOutsourceForm,
} from "./outsource-form-ui";

const OutsourceFormSchema = z
  .object({
    staffPickId: z.string().optional(),
    categoryId: z.string().min(1, "ເລືອກຫມວດ"),
    payeeType: z.enum(["individual", "team"]),
    displayName: z.string().trim().min(1, "ຕ້ອງປ້ອນຊື່"),
    contactName: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),
    bankName: z.string().optional(),
    bankAccount: z.string().optional(),
    bankAccountName: z.string().optional(),
    notes: z.string().optional(),
    status: z.enum(["active", "inactive"]).optional(),
  })
  .superRefine((vals, ctx) => {
    if (vals.payeeType === "individual" && vals.contactName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contactName"],
        message: "ບຸກຄົນ — ບໍ່ຕ້ອງປ້ອນຜູ້ຕິດຕໍ່",
      });
    }
  });

export type OutsourceFormValues = z.infer<typeof OutsourceFormSchema>;

type OutsourceFormProps = {
  initialValues?: Partial<OutsourceFormValues>;
  onSubmit: (values: OutsourceFormValues) => void;
  onCancel?: () => void;
  submitting?: boolean;
  isEdit?: boolean;
};

export function OutsourceForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  isEdit = false,
}: OutsourceFormProps) {
  const categoriesQuery = useOutsourceCategoriesQuery(true);
  const categories = categoriesQuery.data?.data ?? [];

  const prevStaffPickIdRef = useRef<string | null>(null);

  const methods = RHF.useForm<OutsourceFormValues>({
    resolver: zodResolver(OutsourceFormSchema),
    defaultValues: {
      staffPickId: "",
      categoryId: initialValues?.categoryId ?? "",
      payeeType: initialValues?.payeeType ?? "individual",
      displayName: initialValues?.displayName ?? "",
      contactName: initialValues?.contactName ?? "",
      phone: initialValues?.phone ?? "",
      email: initialValues?.email ?? "",
      bankName: initialValues?.bankName ?? "",
      bankAccount: initialValues?.bankAccount ?? "",
      bankAccountName: initialValues?.bankAccountName ?? "",
      notes: initialValues?.notes ?? "",
      status: initialValues?.status ?? "active",
    },
  });

  const payeeType = methods.watch("payeeType");
  const categoryId = methods.watch("categoryId");
  const displayName = methods.watch("displayName");
  const staffPickId = methods.watch("staffPickId");
  const selectedCategory = categories.find((c) => c.id === categoryId);
  const { data: staffDetail } = useStaffQuery(staffPickId ?? "");

  useEffect(() => {
    if (isEdit) return;
    if (selectedCategory?.defaultPayeeType) {
      methods.setValue("payeeType", selectedCategory.defaultPayeeType);
    }
  }, [selectedCategory?.defaultPayeeType, isEdit, methods]);

  useEffect(() => {
    if (isEdit || !staffPickId || !staffDetail) return;
    if (staffPickId === prevStaffPickIdRef.current) return;
    prevStaffPickIdRef.current = staffPickId;
    applyStaffToOutsourceForm(methods.setValue, staffDetail, "master");
  }, [isEdit, staffPickId, staffDetail, methods]);

  return (
    <FormRoot
      methods={methods}
      onSubmit={(vals) => {
        onSubmit({
          ...vals,
          contactName:
            vals.payeeType === "team" ? vals.contactName?.trim() || null : null,
        });
      }}
      className="space-y-5 py-6"
    >
      <OutsourceFormPreview
        mode="master"
        displayName={displayName}
        payeeType={payeeType}
        categoryName={selectedCategory?.nameLo}
        categorySlug={selectedCategory?.slug}
      />

      {!isEdit ? <OutsourceStaffPickField name="staffPickId" /> : null}

      <OutsourceCategoryTypeSection categories={categories} />
      <OutsourceIdentityFields payeeType={payeeType} />
      <OutsourceContactFields />
      <OutsourceBankFields />
      <OutsourceNotesField />

      {isEdit ? (
        <div className="rounded-xl border bg-muted/20 p-4 sm:p-5">
          <FormSelect
            name="status"
            label="ສະຖານະ master"
            options={[
              { value: "active", label: "ໃຊ້ງານ — ເລືອກໃນ Event ได้" },
              { value: "inactive", label: "ປິດໃຊ້ — ບໍ່ໃຫ້ເລືອກໃນ Event" },
            ]}
          />
        </div>
      ) : null}

      <div className="flex justify-end gap-2 border-t pt-4">
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting
            ? "ກຳລັງບັນທຶກ..."
            : isEdit
              ? "ບັນທຶກ"
              : "ສ້າງ Outsource"}
        </Button>
      </div>
    </FormRoot>
  );
}
